import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ClipboardCheck, Loader2, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { HelpHint } from '../components/HelpHint';
import { PageNav } from '../components/PageNav';
import { api } from '../lib/api';

/**
 * Counting what is actually on the shelf.
 *
 * ## Why the expected figure is frozen
 *
 * The count was opened against a snapshot of the ledger. Somebody issuing
 * stock while a count is in progress is normal and must not silently change
 * what the person counting was measured against — otherwise a count that
 * balanced perfectly reports a discrepancy created by a colleague two rooms
 * away.
 *
 * ## Why the variance is priced
 *
 * "Three ONTs missing" is a shrug. "KES 21,000 missing" is a conversation, and
 * it is the same fact. Small differences on cable and consumables are shown but
 * not flagged: counting a drum to the metre is not realistic, and marking
 * measurement error as a discrepancy trains people to ignore the whole report.
 */
export function StockCountPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<string | null>(null);

  const count = useQuery({
    queryKey: ['stock-count', id],
    queryFn: () => api.stockCount(id),
    enabled: Boolean(id),
  });

  const setLine = useMutation({
    mutationFn: ({ code, counted }: { code: string; counted: number }) =>
      api.setStockCountLine(id, code, counted),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stock-count', id] }),
    onError: (e: Error) => setError(e.message),
  });

  const apply = useMutation({
    mutationFn: () => api.applyStockCount(id),
    onSuccess: async (result) => {
      setError(null);
      setApplied(result.summary);
      await queryClient.invalidateQueries({ queryKey: ['stock-count', id] });
      await queryClient.invalidateQueries({ queryKey: ['stock-overview'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (count.isPending) {
    return (
      <div className="space-y-6">
        <PageNav crumbs={[{ label: 'Stock', to: '/stock' }, { label: 'Count' }]} />
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading the count
        </p>
      </div>
    );
  }

  const data = count.data;
  if (!data) return null;

  const done = Boolean(data.appliedAt);
  const material = data.variance.filter((row) => row.material);

  return (
    <div className="space-y-6">
      <PageNav crumbs={[{ label: 'Stock', to: '/stock' }, { label: data.locationName }]} />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-1.5 font-heading text-2xl">
            Counting {data.locationName}
            <HelpHint>
              The expected column was frozen when this count was opened, so anybody moving stock
              while you count cannot change what you are measured against.
            </HelpHint>
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Opened by {data.countedBy}. {data.summary}
          </p>
        </div>

        {!done && (
          <button
            type="button"
            className="btn-primary"
            disabled={apply.isPending}
            onClick={() => apply.mutate()}
          >
            {apply.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <ClipboardCheck className="h-4 w-4" aria-hidden="true" />
            )}
            Apply the corrections
          </button>
        )}
      </div>

      {applied && (
        <p className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {applied}
        </p>
      )}
      {done && !applied && (
        <p className="rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          Applied on {new Date(data.appliedAt!).toLocaleString('en-GB')}. This count is closed and
          its figures cannot change.
        </p>
      )}
      {error && (
        <p className="rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {error}
        </p>
      )}

      {material.length > 0 && !done && (
        <div className="card border-amber-200 bg-amber-50/50">
          <h2 className="flex items-center gap-2 font-heading text-lg">
            <TriangleAlert className="h-4 w-4 text-amber-700" aria-hidden="true" />
            Worth looking into before you apply this
          </h2>
          <ul className="mt-2 space-y-1 text-sm">
            {material.map((row) => (
              <li key={row.code} className="flex flex-wrap items-baseline gap-2">
                <span className="font-medium">{row.name}</span>
                <span className="text-muted-foreground">
                  expected {row.expected}, counted {row.counted}
                </span>
                <span
                  className={`ml-auto tabular-nums ${row.varianceValueCents < 0 ? 'text-danger-700' : 'text-emerald-700'}`}
                >
                  {row.value}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs label-mono tracking-[0.12em] text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Item</th>
              <th className="px-4 py-3 text-right">System says</th>
              <th className="px-4 py-3 text-right">On the shelf</th>
              <th className="px-4 py-3 text-right">Difference</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((line) => {
              const difference = line.counted - line.expected;
              return (
                <tr key={line.id} className="border-t border-border">
                  <td className="px-4 py-2">
                    {line.name}
                    {line.unit === 'metre' && (
                      <span className="ml-1 text-xs text-muted-foreground">(metres)</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                    {line.expected}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <input
                      className="input w-24 text-right tabular-nums"
                      inputMode="decimal"
                      defaultValue={line.counted}
                      disabled={done || setLine.isPending}
                      onBlur={(event) => {
                        const counted = Number(event.target.value);
                        if (Number.isFinite(counted) && counted !== line.counted) {
                          setError(null);
                          setLine.mutate({ code: line.code, counted });
                        }
                      }}
                    />
                  </td>
                  <td
                    className={`px-4 py-2 text-right tabular-nums ${
                      difference === 0
                        ? 'text-muted-foreground'
                        : difference < 0
                          ? 'text-danger-700'
                          : 'text-emerald-700'
                    }`}
                  >
                    {difference === 0 ? '—' : `${difference > 0 ? '+' : ''}${difference}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
